import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './FramesCarousel.css'
import { StripItem } from '../../types'

interface FramesCarouselProps {
  frames: StripItem[]
  initialIndex: number
  onClose: () => void
}

export default function FramesCarousel({ frames, initialIndex, onClose }: FramesCarouselProps) {
  const { t } = useTranslation()
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const touchStartX = useRef<number>(0)
  const multipleFrames = frames.length > 1

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [onClose])

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentIndex > 0) setCurrentIndex(i => i - 1)
  }

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (currentIndex < frames.length - 1) setCurrentIndex(i => i + 1)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current
    if (delta > 50 && currentIndex > 0) setCurrentIndex(i => i - 1)
    if (delta < -50 && currentIndex < frames.length - 1) setCurrentIndex(i => i + 1)
  }

  return (
    <div className="frames-carousel" onClick={onClose}>
      <button
        className="frames-carousel__close"
        onClick={(e) => { e.stopPropagation(); onClose() }}
        aria-label={t('frames_carousel.close')}
      >
        <span className="frames-carousel__close-visual">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      <div
        className="frames-carousel__image-wrapper"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          className="frames-carousel__image"
          src={frames[currentIndex].url}
          alt={t('frames_carousel.alt', { index: currentIndex + 1, total: frames.length })}
        />
      </div>

      {multipleFrames && (
        <button
          className={`frames-carousel__arrow frames-carousel__arrow--prev${currentIndex === 0 ? ' frames-carousel__arrow--disabled' : ''}`}
          onClick={handlePrev}
          aria-label={t('frames_carousel.prev')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {multipleFrames && (
        <button
          className={`frames-carousel__arrow frames-carousel__arrow--next${currentIndex === frames.length - 1 ? ' frames-carousel__arrow--disabled' : ''}`}
          onClick={handleNext}
          aria-label={t('frames_carousel.next')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 4L13 10L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {multipleFrames && (
        <div className="frames-carousel__indicator" onClick={(e) => e.stopPropagation()}>
          {currentIndex + 1} / {frames.length}
        </div>
      )}
    </div>
  )
}
